import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import { PARTNER_OFFERINGS } from "../../data/partnerOfferings";
import "./NJWealthPage.css";

const NJ_WEALTH_ACCOUNT_OPEN_URL = "http://p.njw.bz/77313";

const NJWealthPage = () => {
  const navigate = useNavigate();
  const partner = PARTNER_OFFERINGS.nj;
  const [openSections, setOpenSections] = useState([0, 1]);
  const [openFaq, setOpenFaq] = useState(0);
  const [activeProduct, setActiveProduct] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleSection = (index) => {
    setOpenSections((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const getItemMeta = (item) => {
    const normalized = item.toLowerCase();
    if (normalized.includes("mutual fund")) {
      return {
        icon: "fa-chart-pie",
        detail:
          "Core NJ strength for SIP-led, long-term, diversified wealth creation.",
      };
    }
    if (normalized.includes("equity") || normalized.includes("etf")) {
      return {
        icon: "fa-chart-line",
        detail:
          "Market-linked participation with flexible execution and portfolio integration.",
      };
    }
    if (normalized.includes("futures") || normalized.includes("options")) {
      return {
        icon: "fa-wave-square",
        detail:
          "Derivative exposure for tactical positioning and risk-managed opportunities.",
      };
    }
    if (normalized.includes("pms") || normalized.includes("nps")) {
      return {
        icon: "fa-briefcase",
        detail:
          "Goal-linked managed and retirement-oriented options for structured planning.",
      };
    }
    if (normalized.includes("loan")) {
      return {
        icon: "fa-hand-holding-dollar",
        detail:
          "Liquidity support through partner-backed financing products and structures.",
      };
    }
    if (normalized.includes("robo") || normalized.includes("goal")) {
      return {
        icon: "fa-robot",
        detail:
          "Digital planning tools for guided, objective, and milestone-based investing.",
      };
    }
    if (normalized.includes("distribution") || normalized.includes("partner")) {
      return {
        icon: "fa-network-wired",
        detail:
          "Large partner ecosystem enabling accessible products with assisted advisory.",
      };
    }
    return {
      icon: "fa-circle-check",
      detail:
        "Integrated within NJ Wealth's distribution-led investment and advisory framework.",
    };
  };

  const productDetails = {
    "Equity delivery & intraday": {
      description:
        "Allows investors to buy and hold equities for delivery or execute same-day intraday trades through NJ's integrated platform.",
      features: [
        "Access to NSE and BSE listed securities",
        "Flexible execution in both delivery and intraday modes",
        "Integrated portfolio tracking in NJ Wealth dashboard",
        "Suitable for both guided and experienced investors",
      ],
    },
    "Equity Futures & Options": {
      description:
        "Derivative access for tactical positioning and risk-managed market participation through structured execution workflows.",
      features: [
        "F&O participation through broker-partner integrations",
        "Supports hedging and directional strategies",
        "Suitable for active investors with defined risk controls",
        "Can be aligned with broader portfolio allocation strategy",
      ],
    },
    "ETFs & direct equity investing": {
      description:
        "Provides exposure through direct stocks and ETF-based models, including NJ's rule-based ETF and equity portfolio approaches.",
      features: [
        "Broad market, sector, and thematic ETF participation",
        "Direct equity access for listed market opportunities",
        "Rule-based investment approaches and periodic rebalancing",
        "Suitable for both retail and HNI investor profiles",
      ],
    },
    "Mutual Funds (ALL AMCs, SIP-focused)": {
      description:
        "Core NJ strength offering access to 40+ AMCs with SIP-led, disciplined, long-term wealth creation workflows.",
      features: [
        "Access to all major AMCs and broad scheme universe",
        "SIP-first approach for systematic wealth creation",
        "Digital transaction stack with UPI and net banking",
        "Risk-profile based ready-made portfolio solutions",
      ],
    },
    "Portfolio Management Services (PMS)": {
      description:
        "Managed portfolio offerings spanning discretionary and non-discretionary approaches under NJ's rule-based framework.",
      features: [
        "Designed for HNIs with strategy-driven execution",
        "Discretionary and non-discretionary portfolio options",
        "Rule-based stock and asset selection framework",
        "Includes bluechip, multicap, ETF, hybrid, and low-risk variants",
      ],
    },
    "National Pension System (NPS)": {
      description:
        "Goal-linked retirement planning product with tax-efficient contribution options and pension-focused structure.",
      features: [
        "Tier I and Tier II account structures",
        "Additional tax benefit under Section 80CCD(1B)",
        "Supports both private and government employee segments",
        "Integrated goal-based retirement tracking approach",
      ],
    },
    "Bonds (limited via partners)": {
      description:
        "Fixed-income options available via NJ's partner ecosystem for conservative and diversification-oriented investors.",
      features: [
        "Access to G-Secs, PSU bonds, and corporate bonds",
        "Sovereign Gold Bonds available through partner rails",
        "Assisted advisory via distributor network",
        "Useful for fixed-income and risk-balance allocation",
      ],
    },
    "Loan Against Mutual Funds (LAS)": {
      description:
        "Liquidity solution allowing borrowing against eligible mutual fund holdings without liquidating long-term investments.",
      features: [
        "Quick processing through digital workflow",
        "LTV-based funding against eligible equity/debt funds",
        "Interest charged on utilized amount (overdraft style)",
        "Generally no prepayment penalty in common structures",
      ],
    },
    "Term loans / flexi loans via NBFC partners": {
      description:
        "Partner-backed financing products for planned and revolving credit needs, integrated with NJ's advisory ecosystem.",
      features: [
        "Term and flexi loan structures through NBFC partners",
        "Use cases include education, business, and medical needs",
        "Competitive rates based on partner eligibility",
        "Advisory support for selecting suitable credit products",
      ],
    },
    "Robo-advisory tools": {
      description:
        "Algorithm-led guidance tools designed to reduce bias and build portfolio pathways aligned with investor profile.",
      features: [
        "Risk profiling questionnaire and model mapping",
        "Automated portfolio recommendations",
        "Rebalancing alerts and progress tracking",
        "Integrated with NJ dashboard for end-to-end visibility",
      ],
    },
    "Goal-based investment planning": {
      description:
        "Structured planning framework that maps investment products to specific life goals and timelines.",
      features: [
        "Goal mapping for retirement, education, home, and wealth creation",
        "Target amount and horizon-based planning model",
        "SIP scheduling for disciplined goal funding",
        "Advisor-assisted and self-directed modes available",
      ],
    },
    "Distributor-led advisory (via NJ partners)": {
      description:
        "Advisory support delivered through one of India's largest distributor ecosystems powered by NJ technology.",
      features: [
        "53,047 active distributors across India",
        "Free CRM and client-management tools for advisors",
        "Ready model portfolios for varied client segments",
        "Training, support, and research enablement for partners",
      ],
    },
    "Financial product distribution platform": {
      description:
        "Large-scale e-wealth platform enabling paperless onboarding, digital execution, and multi-product distribution.",
      features: [
        "One platform for MF, PMS, equity, bonds, NPS, and loans",
        "Paperless onboarding and digital transaction support",
        "SIP, SWP, and STP systematic transaction capabilities",
        "Real-time valuation and centralized portfolio visibility",
      ],
    },
    "Partner-led wealth advisory network": {
      description:
        "A broad partner ecosystem connecting investors with advisors, technology rails, and product execution infrastructure.",
      features: [
        "Pan-India partner-led advisory reach",
        "Technology-enabled advisor workflows",
        "Training, certification, and marketing support framework",
        "Research and model portfolio access for advisors",
      ],
    },
    "Client portfolio tracking tools": {
      description:
        "Comprehensive digital tracking stack for holdings, SIPs, statements, tax reporting, and milestone monitoring.",
      features: [
        "Consolidated portfolio view across asset classes",
        "Real-time NAV and valuation monitoring",
        "Transaction history and capital gains reporting",
        "Goal progress alerts and review notifications",
      ],
    },
  };

  const FAQS = [
    {
      q: "What services are offered through your partnership with NJ Wealth?",
      a: "Through our NJ Wealth partnership, we offer market access, mutual fund distribution, PMS/NPS options, partner-led advisory, and portfolio tracking support.",
    },
    {
      q: "How does NJ Wealth benefit clients?",
      a: "Clients get access to a broad distribution platform, strong SIP-focused mutual fund ecosystem, and guided support through a partner advisory network.",
    },
    {
      q: "What investment options are available?",
      a: "Options include equities, ETFs, equity derivatives, mutual funds across AMCs, PMS, NPS, and selected bond products through partner channels.",
    },
    {
      q: "Do you provide advisory services with NJ Wealth?",
      a: "Yes. We provide distributor-led advisory along with goal-based planning and research-guided recommendations aligned with your profile.",
    },
    {
      q: "Can beginners start investing through this platform?",
      a: "Absolutely. We help with onboarding, profile-based product selection, SIP setup, and foundational investment guidance for first-time investors.",
    },
    {
      q: "Do clients get access to digital tools?",
      a: "Yes. Clients can access digital tools for portfolio tracking, goal planning, and streamlined investment execution workflows.",
    },
    {
      q: "How do I open an NJ Wealth account?",
      a: "Account opening is completed through a simple KYC-compliant process, either online or with our assisted onboarding support.",
    },
    {
      q: "Are there minimum investment requirements?",
      a: "Minimum amounts vary by product. SIPs can start small, while PMS and other products may have higher thresholds based on provider norms.",
    },
    {
      q: "What type of insights are shared with clients?",
      a: "Clients receive market updates, product insights, portfolio-level views, and periodic recommendations based on evolving goals and market conditions.",
    },
    {
      q: "Do you provide portfolio monitoring support?",
      a: "Yes. We provide ongoing portfolio tracking and review recommendations to improve alignment with risk profile and long-term objectives.",
    },
    {
      q: "How secure are transactions on NJ platforms?",
      a: "Transactions are processed through regulated and secure digital channels with standard compliance, authentication, and data protection controls.",
    },
    {
      q: "How can clients get support?",
      a: "Clients can contact our team for personalized support and guidance, and also use NJ Wealth platform support channels for account/platform queries.",
    },
  ];

  const overviewStats = [
    { label: "Founded", value: "1994" },
    { label: "Founders", value: "Niraj Choksi & Jignesh Desai" },
    { label: "Employees", value: "2,578" },
    { label: "Active Distributors", value: "53,047" },
    { label: "AUM (Mar 2026)", value: "Rs 2,61,238 Cr" },
    { label: "Flagship", value: "NJ India Invest Pvt. Ltd." },
  ];

  const uspItems = [
    "Built on Rules - 100% rule-based investing with no discretionary bias.",
    "Quality First - Focus on high ROE, high dividend payout, and low debt businesses.",
    "Scale Advantage - 53,000+ active distributors and deep pan-India reach.",
    "Closed-Loop Ecosystem - Distribution to AMC, PMS, loans, and global investing.",
    "True-to-Label Portfolios - Low overlap and high active share vs benchmarks.",
    "Ready-to-Use Advisory Platform - Faster onboarding and consistent advisory outcomes.",
  ];

  const milestoneTimeline = [
    "2009 Jul - First rule-based active investment approach with MF schemes.",
    "2010 Dec - Approval to launch PMS business.",
    "2016 Apr - Launch of Multicap Portfolio and rule-based equity stock approach.",
    "2018 Mar - Launch of Bluechip Portfolio.",
    "2018 Aug - Launch of Freedom ETF Portfolio.",
    "2021 Apr - PMS AUM crossed Rs 1,000 Cr and MF scheme approval received.",
    "2021 Oct - Total AUM crossed Rs 10,000 Cr.",
    "2024 Aug - Launch of Non-Discretionary PMS.",
    "2026 Mar - PMS AUM Rs 3,890 Cr and 6,056 PMS investors.",
  ];

  const philosophyPrinciples = [
    "Rule-Based Investing",
    "Quality Focused",
    "Transparency",
    "Customer Centric",
    "Trust",
    "True-To-Label",
  ];

  const businessModelPoints = [
    "Primarily a distribution-led financial platform.",
    "Operates via a large advisor and MFD network across India.",
    "Technology-first workflow for onboarding, transactions, and tracking.",
    "Serves both end-investors and distributors in one ecosystem.",
    "Recurring annuity-led revenue model.",
  ];

  const investmentProcessSteps = [
    "Start with NIFTY 500 as the base universe.",
    "Apply governance and forensic eliminations.",
    "Remove highly volatile and anti-quality names.",
    "Shortlist quality companies using ROE, dividend payout, and D/E.",
    "Apply proprietary business-specific value scoring.",
    "Construct final portfolio from undervalued quality opportunities.",
    "Manage risk using stock and sector exposure caps.",
  ];

  const smartFactorShiftModes = [
    {
      title: "Risk-On",
      desc: "Higher momentum exposure during strong, low-volatility markets.",
    },
    {
      title: "Risk-Off",
      desc: "Higher low-volatility exposure during weak or volatile phases.",
    },
    {
      title: "Extreme Valuation",
      desc: "Higher value / defensive tilt in stretched market conditions.",
    },
  ];

  const mfSuite = [
    "NJ Flexi Cap Fund - 50-stock dynamic rule-based equity strategy.",
    "NJ ELSS Tax Saver Scheme - 80C benefit with quality-momentum tilt.",
    "NJ Balanced Advantage Fund - Dynamic equity, debt, and arbitrage mix.",
    "NJ Arbitrage Fund - Cash-futures arbitrage with lower risk profile.",
    "NJ Overnight Fund - TREPS-oriented high-liquidity debt positioning.",
  ];

  return (
    <div className="nj-wrapper">
      <Navbar />

      <section className="nj-hero">
        <div className="nj-hero-bg" />
        <div className="nj-container nj-hero-inner">
          <button className="nj-back-btn" onClick={() => navigate("/wealth")}>
            <i className="fas fa-arrow-left" /> Back to Wealth
          </button>

          <div className="nj-hero-grid">
            <div className="nj-hero-copy">
              <p className="nj-eyebrow">Detailed Partner View</p>
              <h1>{partner.name}</h1>
              <p className="nj-subtitle">{partner.subtitle}</p>
              <p className="nj-intro">{partner.intro}</p>

              <a
                className="nj-account-btn"
                href={NJ_WEALTH_ACCOUNT_OPEN_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Create NJ Wealth Account
                <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
              </a>

              <div className="nj-stat-row">
                <div className="nj-stat-card">
                  <span className="nj-stat-val">5</span>
                  <span className="nj-stat-lbl">Core Offering Segments</span>
                </div>
                <div className="nj-stat-card">
                  <span className="nj-stat-val">20+</span>
                  <span className="nj-stat-lbl">Catalog Product Lines</span>
                </div>
                <div className="nj-stat-card">
                  <span className="nj-stat-val">SIP</span>
                  <span className="nj-stat-lbl">Distribution-Led Core Focus</span>
                </div>
              </div>
            </div>

            <div className="nj-hero-visual">
              <div className="nj-logo-wrap">
                <img src={partner.logo} alt={`${partner.name} logo`} className="nj-logo" />
              </div>
              <div className="nj-graph-card">
                <div className="nj-graph-header">Platform Strength Areas</div>
                <div className="nj-bars">
                  {[86, 94, 78, 82, 88].map((h, i) => (
                    <div key={i} className="nj-bar-col">
                      <div className="nj-bar" style={{ height: `${h}%` }} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="nj-chip nj-chip-1">Mutual Funds</div>
              <div className="nj-chip nj-chip-2">Advisory</div>
              <div className="nj-chip nj-chip-3">Distribution</div>
            </div>
          </div>
        </div>
      </section>

      <section className="nj-details">
        <div className="nj-container">
          <div className="nj-overview-grid">
            <article className="nj-overview-card">
              <h3>NJ Group Business Overview</h3>
              <p>
                NJ Group is one of India&apos;s largest financial distribution-led
                platforms operating across wealth, AMC, insurance, NBFC, and
                technology verticals.
              </p>
              <div className="nj-kpi-grid">
                {overviewStats.map((item) => (
                  <div className="nj-kpi" key={item.label}>
                    <span>{item.value}</span>
                    <small>{item.label}</small>
                  </div>
                ))}
              </div>
            </article>

            <article className="nj-overview-card">
              <h3>Core USP Highlights (From Document)</h3>
              <div className="nj-usp-grid">
                {uspItems.map((item) => (
                  <div className="nj-usp-card" key={item}>
                    <i className="fas fa-star" />
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </article>
          </div>

          <div className="nj-overview-grid nj-overview-grid--secondary">
            <article className="nj-overview-card">
              <h3>Business Model</h3>
              <ul className="nj-doc-list">
                {businessModelPoints.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="nj-overview-card">
              <h3>Asset Management Journey</h3>
              <div className="nj-timeline-list">
                {milestoneTimeline.map((item) => (
                  <div className="nj-timeline-item" key={item}>
                    <span className="nj-timeline-dot" />
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </article>
          </div>

          <div className="nj-detail-head">
            <h2>Complete Product Catalog</h2>
            <p>Expand each segment to view all product lines and capabilities.</p>
          </div>

          <div className="nj-accordion">
            {partner.sections.map((section, index) => {
              const isOpen = openSections.includes(index);
              return (
                <article className={`nj-acc-item ${isOpen ? "nj-open" : ""}`} key={section.heading}>
                  <button
                    className="nj-acc-trigger"
                    onClick={() => toggleSection(index)}
                    aria-expanded={isOpen}
                  >
                    <span>{section.heading}</span>
                    <i className={`fas ${isOpen ? "fa-minus" : "fa-plus"}`} />
                  </button>
                  {isOpen && (
                    <div className="nj-acc-body">
                      <div className="nj-point-grid">
                        {section.items.map((item) => {
                          const meta = getItemMeta(item);
                          return (
                            <article
                              className="nj-point-card"
                              key={`${section.heading}-${item}`}
                              onClick={() => setActiveProduct(item)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  setActiveProduct(item);
                                }
                              }}
                            >
                              <div className="nj-point-icon">
                                <i className={`fas ${meta.icon}`} />
                              </div>
                              <div className="nj-point-content">
                                <h4>{item}</h4>
                                <p>{meta.detail}</p>
                                <span className="nj-point-link">
                                  View details <i className="fas fa-arrow-up-right-from-square" />
                                </span>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          <div className="nj-principles">
            <div className="nj-principles-head">
              <p className="nj-principles-eyebrow">Investment Framework</p>
              <h3>Investment Philosophy - Built on Rules</h3>
              <p>
                A disciplined, process-led framework focused on quality filters,
                transparent rules, and risk-managed portfolio construction.
              </p>
            </div>
            <div className="nj-principle-grid">
              {philosophyPrinciples.map((item) => (
                <div className="nj-principle-card" key={item}>
                  <i className="fas fa-badge-check" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <div className="nj-principles-band">
              <div>
                <strong>No Human Bias</strong>
                <span>Pre-defined rules drive decisions</span>
              </div>
              <div>
                <strong>Quality First</strong>
                <span>ROE, payout, and debt filters</span>
              </div>
              <div>
                <strong>True-To-Label</strong>
                <span>Mandate-consistent portfolios</span>
              </div>
            </div>
          </div>
<br></br>
          <div className="nj-overview-grid nj-overview-grid--secondary">
            <article className="nj-overview-card">
              <h3>Investment Process - Common Methodology</h3>
              <ol className="nj-process-list">
                {investmentProcessSteps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
            </article>

            <article className="nj-overview-card">
              <h3>Smart FactorShift Model</h3>
              <div className="nj-factor-grid">
                {smartFactorShiftModes.map((mode) => (
                  <div className="nj-factor-card" key={mode.title}>
                    <h4>{mode.title}</h4>
                    <p>{mode.desc}</p>
                  </div>
                ))}
              </div>
            </article>
          </div>

          <article className="nj-overview-card nj-overview-card--full">
            <h3>NJ Mutual Fund Product Suite Highlights</h3>
            <div className="nj-mf-suite">
              {mfSuite.map((item) => (
                <div className="nj-mf-item" key={item}>
                  <i className="fas fa-chart-simple" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>

      <section className="nj-faq">
        <div className="nj-container">
          <div className="nj-detail-head">
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know before getting started.</p>
          </div>

          <div className="nj-faq-list">
            {FAQS.map((item, index) => {
              const isOpen = openFaq === index;
              return (
                <article className={`nj-faq-item ${isOpen ? "nj-open" : ""}`} key={item.q}>
                  <button
                    className="nj-faq-trigger"
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                    aria-expanded={isOpen}
                  >
                    <span>{item.q}</span>
                    <i className={`fas ${isOpen ? "fa-minus" : "fa-plus"}`} />
                  </button>
                  {isOpen && (
                    <div className="nj-faq-body">
                      <p>{item.a}</p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {activeProduct && (
        <div className="nj-modal-overlay" onClick={() => setActiveProduct(null)}>
          <div className="nj-modal" onClick={(e) => e.stopPropagation()}>
            <button className="nj-modal-close" onClick={() => setActiveProduct(null)}>
              <i className="fas fa-xmark" />
            </button>
            <p className="nj-modal-eyebrow">Product Details</p>
            <h3>{activeProduct}</h3>
            <div className="nj-modal-block">
              <h4>Product Description</h4>
              <p>
                {productDetails[activeProduct]?.description ||
                  "Detailed product information is available through our advisory team and NJ partner documentation."}
              </p>
            </div>
            <div className="nj-modal-block">
              <h4>Key Features</h4>
              <ul>
                {(productDetails[activeProduct]?.features || [
                  "Structured access through NJ Wealth partner framework",
                  "Advisory-led product suitability mapping",
                  "Integrated dashboard visibility and tracking",
                ]).map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default NJWealthPage;
